import React, { useEffect, useMemo, useState } from "react";

import { DatePicker } from "rsuite";
import "rsuite/DatePicker/styles/index.css";

import { serverUrl } from "../../../../config";
import { getOnlineNode } from "../../../../core/getNode";

import { Col, Container, Row } from "react-bootstrap";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
} from "@mui/material";
import DialogPaper from "../../../../partials/dialogPaper";

// constants
const toMidnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const MIN_FROM = new Date("2025-07-01");
const TODAY = toMidnight(new Date());

export default function Analytics({
  modalVisible,
  onHide,
  presentation,
  user,
}) {
  const [data, setData] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [isError, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [from, setFrom] = useState(new Date("2025-07-01"));
  const [to, setTo] = useState(new Date());

  const nf = useMemo(() => new Intl.NumberFormat("en-GB"), []);
  const fmtTime = (sec) => {
    if (!sec || sec <= 0) return "0s";
    const s = Math.round(sec);
    const m = Math.floor(s / 60);
    const r = s % 60;
    return m ? `${m}m ${r}s` : `${r}s`;
  };

  useEffect(() => {
    if (modalVisible && presentation?.id) getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalVisible, presentation?.id, from?.getTime?.(), to?.getTime?.()]);

  const hideModal = () => onHide?.(false);

  const getData = async () => {
    setLoading(true);
    setError(false);
    setErrorMessage("");
    try {
      const toYmd = (d) =>
        d
          ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
              2,
              "0"
            )}-${String(d.getDate()).padStart(2, "0")}`
          : null;
      const qs = new URLSearchParams({
        id: String(presentation.id),
        from: toYmd(from) ?? "2025-07-01",
        to: toYmd(to) ?? toYmd(new Date()),
      }).toString();

      const res = await getOnlineNode(
        `${serverUrl}/api/v2/ga4/pages-screens?${qs}`
      );
      setData(res.data || null);
    } catch (e) {
      setError(true);
      setErrorMessage(
        e?.response?.status === 403
          ? "You don’t have permission to view analytics."
          : "An error occurred while fetching data."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={modalVisible}
      onClose={hideModal}
      className="ps-modal share-modal"
      maxWidth="md"
      fullWidth
      PaperComponent={DialogPaper}
    >
      <Box className="ps-modal-title dialog-draggable-handle">
        <Container fluid>
          <Row className="align-items-center">
            <Col xs={12} sm={6}>
              <Typography variant="h3" owner="admin" color="adminText.dark">
                Presentation Analytics
              </Typography>
            </Col>
            <Col xs={12} sm={6}>
              <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                spacing={1}
              >
                <Button
                  variant="outlined"
                  owner="admin"
                  dialogbtn="true"
                  dialogvariant="cancel"
                  className="lh"
                  onClick={hideModal}
                >
                  <Typography variant="button" owner="admin" className="lh">
                    Close
                  </Typography>
                </Button>
              </Stack>
            </Col>
          </Row>
        </Container>
      </Box>

      <DialogContent>
        <Box className="ps-modal-content" sx={{ mb: 2 }}>
          <Container fluid>
            <Row className="ps-relative">
              <Col>
                {isLoading && (
                  <Box className="loading-overlay text-center">
                    <CircularProgress />
                  </Box>
                )}

                {isError && (
                  <Alert severity="error" className="mb-3">
                    {errorMessage || "An error occurred while fetching data."}
                  </Alert>
                )}

                {!isLoading && !isError && (
                  <>
                    <Stack
                      direction="row"
                      justifyContent="center"
                      alignItems="center"
                      spacing={4}
                      sx={{ mt: 2, mb: 3 }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                          variant="body1"
                          owner="adminText.dark"
                          align="center"
                        >
                          From:
                        </Typography>
                        <Box sx={{ width: 180 }}>
                          <DatePicker
                            oneTap
                            value={from}
                            onChange={(d) => {
                              const next = d || MIN_FROM;
                              // clamp to min
                              const safeFrom =
                                next < MIN_FROM ? MIN_FROM : next;
                              setFrom(safeFrom);
                              // keep range valid
                              if (to && safeFrom > to) setTo(safeFrom);
                            }}
                            format="yyyy-MM-dd"
                            cleanable={false}
                            // prevent dates before 2025-07-01 and after current "to"/today
                            shouldDisableDate={(d) =>
                              d < MIN_FROM || d > (to || TODAY)
                            }
                            style={{ width: "100%" }}
                          />
                        </Box>
                      </Stack>

                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                          variant="body1"
                          owner="adminText.dark"
                          align="center"
                        >
                          To:
                        </Typography>
                        <Box sx={{ width: 180 }}>
                          <DatePicker
                            oneTap
                            value={to}
                            onChange={(d) => setTo(d || TODAY)}
                            format="yyyy-MM-dd"
                            cleanable={false}
                            // prevent before "from" (and before min) and after today
                            shouldDisableDate={(d) => {
                              const x = toMidnight(d);
                              return x < (from || MIN_FROM) || x > TODAY;
                            }}
                            style={{ width: "100%" }}
                          />
                        </Box>
                      </Stack>
                    </Stack>

                    {data && typeof data.views === "number" ? (
                      <TableContainer component={Paper}>
                        <Table
                          sx={{ minWidth: 650 }}
                          aria-label="analytics table"
                        >
                          <TableHead>
                            <TableRow>
                              <TableCell align="center">
                                <b>
                                  Total
                                  <br />
                                  Views
                                </b>
                              </TableCell>
                              <TableCell align="center">
                                <b>
                                  Active
                                  <br />
                                  users
                                </b>
                              </TableCell>
                              <TableCell align="center">
                                <b>
                                  Views per
                                  <br />
                                  active user
                                </b>
                              </TableCell>
                              <TableCell align="center">
                                <b>
                                  Avg. time per
                                  <br />
                                  active user
                                </b>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell align="center">
                                {nf.format(data.views || 0)}
                              </TableCell>
                              <TableCell align="center">
                                {nf.format(data.activeUsers || 0)}
                              </TableCell>
                              <TableCell align="center">
                                {(Number(data.viewsPerActiveUser) || 0).toFixed(
                                  2
                                )}
                              </TableCell>
                              <TableCell align="center">
                                {fmtTime(
                                  Number(data.avgEngagementPerActiveUserSec) ||
                                    0
                                )}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Alert severity="info" className="mb-3">
                        No data found.
                      </Alert>
                    )}
                  </>
                )}
              </Col>
            </Row>
          </Container>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
